import React, { Fragment, useEffect, useState } from "react";
import { Button, Row, Col, Form, Input, message, Select, TimePicker, Alert } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import moment from "moment";
import Image from 'helpers/Image';
import "../style.css";
import { useAddShift, useEditShift, useGetShift } from "hooks/configuration/shift/shift";
import { formatDuration } from "./index";

const TIME_FORMAT = "HH:mm";
// Matches attendance.max-shift-duration-hours on the API: longer shifts are not used to close a day.
const MAX_SHIFT_HOURS = 12;

const status = [
    { 'label': "Active", "value": true },
    { 'label': "InActive", "value": false },
];

/** Minutes from start to end, wrapping past midnight. */
const durationBetween = (start, end) => {
    if (!start || !end) return null;
    const s = start.hours() * 60 + start.minutes();
    const e = end.hours() * 60 + end.minutes();
    return ((e - s) % 1440 + 1440) % 1440;
};

const FormShift = () => {

    const navigate = useNavigate();
    const [form] = Form.useForm();
    const { id } = useParams();
    const create = id === "create";
    const [serverErrors, setServerErrors] = useState({});
    const [loaded, setLoaded] = useState(null);

    const { mutate: addShift, isLoading: addLoading } = useAddShift();
    const { mutate: editShift, isLoading: editLoading } = useEditShift();
    const { mutate: getShift } = useGetShift();

    const start = Form.useWatch("startTime", form);
    const end = Form.useWatch("endTime", form);
    const minutes = durationBetween(start, end);
    const overnight = start && end && !end.isAfter(start);

    useEffect(() => {
        if (create) return;
        getShift(id, {
            onSuccess: (item) => {
                if (item?.statusCode !== 200) return;
                const shift = item.data;
                setLoaded(shift);
                form.setFieldsValue({
                    shiftId: shift.shiftId,
                    shiftName: shift.shiftName,
                    startTime: moment(shift.startTime, TIME_FORMAT),
                    endTime: moment(shift.endTime, TIME_FORMAT),
                    active: shift.status,
                });
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const onDone = (item) => {
        if (item?.statusCode === 200) {
            // A long message carries the "longer than 12 h" note, so give it time to be read.
            message.success(item.message, item.message.length > 40 ? 8 : 3);
            navigate("/configuration/shift");
        }
    };

    const onError = (error) => {
        if (error?.response?.data?.errorMessages)
            setServerErrors(error.response.data.errorMessages);
    };

    const onFinish = (values) => {
        const submitData = {
            shiftName: values.shiftName ? values.shiftName.trim().split(/ +/).join(' ') : null,
            startTime: values.startTime.format(TIME_FORMAT),
            endTime: values.endTime.format(TIME_FORMAT),
        };
        if (create) {
            addShift(JSON.stringify(submitData), { onSuccess: onDone, onError });
        } else {
            editShift(JSON.stringify({ ...submitData, shiftId: values.shiftId, active: values.active }), { onSuccess: onDone, onError });
        }
    };

    const clearError = (name) => setServerErrors(prev => ({ ...prev, [name]: "" }));

    const title = create
        ? "New Shift"
        : `Edit ${loaded?.shiftName || (loaded ? `${loaded.startTime} - ${loaded.endTime}` : "shift")}`;

    return (
        <Fragment>
            <div className="form-page">
                <div className="form-head form-head-sticky">
                    <div className="form-head-left">
                        <Link to="/configuration/shift" className="form-back">
                            <Image name="backArrow.svg" />
                        </Link>
                        <div className="form-title">
                            <h1>{title}</h1>
                            <p>{create ? "Add working hours that employees can be assigned to" : "Update this shift's name, hours or status"}</p>
                        </div>
                    </div>
                    <div className="form-actions">
                        <Button className="form-cancel" onClick={() => navigate("/configuration/shift")}>Cancel</Button>
                        <Button className="form-submit" loading={addLoading || editLoading} onClick={() => form.submit()} htmlType="submit">
                            {addLoading || editLoading ? null : <Image className="save-img" name="save.svg" />} {create ? "Save" : "Update"}
                        </Button>
                    </div>
                </div>

                <div className="form-card">
                    <Form form={form} onFinish={onFinish} colon={false} layout="vertical">
                        <Form.Item name="shiftId" hidden={true}>
                            <Input />
                        </Form.Item>

                        <section className="form-section">
                            <div className="form-section-head">
                                <h2>Shift details</h2>
                                <p>Times are site time (24-hour). An end time at or before the start makes an overnight shift.</p>
                            </div>
                            <Row gutter={[24, 0]}>
                                <Col xs={24} sm={12} lg={6}>
                                    <Form.Item label="Shift Name (optional)" name="shiftName"
                                        validateStatus={serverErrors?.shiftName ? "error" : ""} help={serverErrors?.shiftName?.[0]}>
                                        <Input placeholder="e.g. Day shift" maxLength={50} onChange={() => clearError("shiftName")} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={6} lg={4}>
                                    <Form.Item label="Start Time" name="startTime" rules={[{ required: true, message: "Start Time is required" }]}
                                        validateStatus={serverErrors?.startTime ? "error" : ""} help={serverErrors?.startTime?.[0]}>
                                        <TimePicker format={TIME_FORMAT} minuteStep={5} style={{ width: "100%" }} placeholder="08:00"
                                            onChange={() => { clearError("startTime"); clearError("endTime"); }} />
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={6} lg={4}>
                                    <Form.Item label="End Time" name="endTime" rules={[{ required: true, message: "End Time is required" }]}
                                        validateStatus={serverErrors?.endTime ? "error" : ""} help={serverErrors?.endTime?.[0]}>
                                        <TimePicker format={TIME_FORMAT} minuteStep={5} style={{ width: "100%" }} placeholder="17:00"
                                            onChange={() => clearError("endTime")} />
                                    </Form.Item>
                                </Col>
                                {create ? null : (
                                    <Col xs={24} sm={12} lg={4}>
                                        <Form.Item label="Status" name="active" rules={[{ required: true, message: 'Required!' }]}>
                                            <Select placeholder="Select status" options={status} />
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>

                            {minutes === null ? null : minutes === 0 ? (
                                <Alert type="error" showIcon message="Start and end are the same time." />
                            ) : (
                                <Alert
                                    type={minutes > MAX_SHIFT_HOURS * 60 ? "warning" : "info"}
                                    showIcon
                                    message={`${formatDuration(minutes)}${overnight ? ", overnight (ends the next day)" : ""}`}
                                    description={minutes > MAX_SHIFT_HOURS * 60
                                        ? `Longer than ${MAX_SHIFT_HOURS} h, so a missing check-out on this shift is closed by the ${MAX_SHIFT_HOURS} h cap, not the shift end.`
                                        : null}
                                />
                            )}

                            {!create && loaded?.employeeCount > 0 ? (
                                <Alert style={{ marginTop: 12 }} type="info" showIcon
                                    message={`${loaded.employeeCount} employee(s) are on this shift. New hours apply to how their missing check-outs are closed from the next nightly run.`} />
                            ) : null}
                        </section>
                    </Form>
                </div>
            </div>
        </Fragment>
    )
}
export default FormShift;
